import React, { ReactNode, useEffect, useState } from "react";
import { TABLES } from "store/userMeta";
import { ConnectionStatusIcon } from "elements";
import { Link } from "polyrouter";
import { handleNullDates, handleNullFields } from "utilities/stringUtils";
import VisibilitySensor from "react-visibility-sensor";
import { TableBulkItemRow } from "collections/Table";
import { ConnectionStatusCell } from "routes/Manage/Inventory/List/TableView";
import { DeviceUserInfo } from "routes/Manage/DeviceUsers/Shared/utils/deviceUserUtils";

export type DeviceUserRowProps = {
  onCheck: (id: string) => void;
  checked: boolean;
  shownColumns: Array<string>;
  showChecks: boolean;
  endCursor?: string;
  index: number;
  userListLength: number;
  hasNextPage?: boolean;
  updateCursor?: (cursor: string) => void;
  loading: boolean;
} & Pick<
  DeviceUserInfo,
  "user_id" | "name" | "lensDesktopInfo" | "lensMobileInfo" | "connectedDevices"
>;

export const DeviceUserRow: React.FC<DeviceUserRowProps> = ({
  name,
  user_id,
  lensDesktopInfo,
  lensMobileInfo,
  connectedDevices,
  index,
  userListLength,
  endCursor,
  hasNextPage,
  checked,
  showChecks,
  onCheck,
  shownColumns,
  updateCursor,
  loading
}) => {
  // track sensor visibility changes so we can determine if
  // we need to paginate when endCursor changes, but none of the new
  // page is rendered. This can happen when client side filtering filters
  // out an entire page.
  const [previouslyVisible, setPreviouslyVisible] = useState(false);

  // if visibility or endCursor changes,
  // we need to check if a new page of data is needed.
  useEffect(() => {
    if (
      index === userListLength - 1 &&
      hasNextPage &&
      previouslyVisible &&
      !loading &&
      updateCursor && // not all usages of DeviceUserRow have pagination
      endCursor
    ) {
      updateCursor(endCursor);
    }
  }, [
    previouslyVisible,
    userListLength,
    endCursor,
    index,
    updateCursor,
    hasNextPage,
    loading
  ]);

  const connected = lensDesktopInfo?.connected;
  const connectionsLength = connectedDevices?.length;

  const primaryDevice =
    connectionsLength && connectedDevices
      ? connectedDevices[connectionsLength - 1]?.name
      : null;

  const { COLUMNS } = TABLES.DEVICE_USERS;
  const getTableContent = (column: string) => {
    let content: ReactNode = null;

    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link
            to={{
              pathname: "/manage/device-users/users/details",
              search: `user_id=${user_id}&desktopId=${lensDesktopInfo?.id ||
                ""}&mobileId=${lensMobileInfo?.id || ""}`
            }}
          >
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.PRIMARY_DEVICE:
        content = <span>{handleNullFields(primaryDevice)}</span>;
        break;
      case COLUMNS.APP_VERSION:
        content = (
          <span>{handleNullFields(lensDesktopInfo?.softwareVersion)}</span>
        );
        break;
      case COLUMNS.DEVICE_COUNT:
        content = <span>{connectionsLength || 0}</span>;
        break;
      case COLUMNS.LAST_DETECTED:
        content = <span>{handleNullDates(lensDesktopInfo?.lastDetected)}</span>;
        break;
    }
    return <td key={column}>{content}</td>;
  };
  return (
    <VisibilitySensor
      offset={{ bottom: -1000 }}
      onChange={setPreviouslyVisible}
    >
      <TableBulkItemRow
        checked={checked}
        onCheck={() => onCheck(user_id)}
        showChecks={showChecks}
      >
        <ConnectionStatusCell>
          <ConnectionStatusIcon connected={!!connected} className="m-0" />
        </ConnectionStatusCell>
        {shownColumns.map(getTableContent)}
      </TableBulkItemRow>
    </VisibilitySensor>
  );
};
